/**
 * @file service-management-service.ts
 * A client-side service for interacting with the Service Management API.
 */

// --- TYPE DEFINITIONS ---
export interface AssetService {
  id: string;  // UUID for security - prevents enumeration attacks
  assetUuid: string;
  serviceSlug: string;
  ownerUserId: string;
  organizationId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AssetServiceWithDetails extends AssetService {
  asset: {
    uuid: string;
    name: string;
    type: string;
    status: string;
    createdAt: string;
    updatedAt: string | null;
    version: number;
    data: unknown;
  } | null;
  organization: {
    id: string;
    name: string;
    slug: string;
  } | null;
  owner: {
    id: string;
    name: string;
    email: string;
  } | null;
}

// --- CONFIGURATION ---
const API_BASE_URL = '/api';

// --- PRIVATE HELPERS ---

/**
 * A generic response handler for fetch calls.
 */
async function handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'An unknown error occurred' }));
        const errorMessage = errorData.error || `Request failed with status ${response.status}`;
        throw new Error(errorMessage);
    }
    return response.json();
}

// --- PUBLIC SERVICE MANAGEMENT SERVICE ---
export const serviceManagementService = {
    /**
     * Creates a new service from an asset.
     * Services are organization-only - users must be members of the organization.
     */
    createService: async (assetUuid: string, serviceData: { 
      serviceSlug: string; 
      organizationId: string;
    }): Promise<AssetService> => {
        const response = await fetch(`${API_BASE_URL}/services/create/${assetUuid}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(serviceData),
        });
        return handleResponse<AssetService>(response);
    },

    /**
     * Retrieves a list of services.
     * Returns services owned by the user in organizations they belong to.
     * If organizationId is provided, returns only services from that organization.
     */
    listServices: async (organizationId?: string, options: { includeInactive?: boolean } = { includeInactive: true }): Promise<AssetServiceWithDetails[]> => {
        const includeInactive = options.includeInactive ?? true;
        const params = new URLSearchParams();

        if (organizationId) {
            params.append('organizationId', organizationId);
        }
        if (includeInactive) {
            params.append('includeInactive', 'true');
        }

        const query = params.toString() ? `?${params.toString()}` : '';
        const response = await fetch(`${API_BASE_URL}/services${query}`, {
            credentials: 'include'
        });
        return handleResponse<AssetServiceWithDetails[]>(response);
    },

    /**
     * Retrieves a specific service by ID (UUID).
     */
    getService: async (id: string): Promise<AssetServiceWithDetails> => {
        const response = await fetch(`${API_BASE_URL}/services/${id}`, {
            credentials: 'include'
        });
        return handleResponse<AssetServiceWithDetails>(response);
    },

    /**
     * Updates a service.
     */
    updateService: async (id: string, updateData: Partial<AssetService>): Promise<AssetService> => {
        const response = await fetch(`${API_BASE_URL}/services/${id}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(updateData),
        });
        return handleResponse<AssetService>(response);
    },

    /**
     * Reactivates a previously unpublished service.
     */
    reactivateService: async (id: string): Promise<AssetService> => {
        return serviceManagementService.updateService(id, { isActive: true } as Partial<AssetService>);
    },

    /**
     * Unpublishes a service (marks as inactive).
     */
    unpublishService: async (id: string): Promise<{ message: string }> => {
        const response = await fetch(`${API_BASE_URL}/services/${id}`, {
            method: 'DELETE',
            credentials: 'include'
        });
        return handleResponse<{ message: string }>(response);
    },

    /**
     * Permanently removes a service.
     */
    removeService: async (id: string): Promise<{ message: string }> => {
        const response = await fetch(`${API_BASE_URL}/services/${id}/permanent`, {
            method: 'DELETE',
            credentials: 'include'
        });
        return handleResponse<{ message: string }>(response);
    },
};
